import { type Subscription } from "../types/subscription";
import { getPeriodStart, type Frequency } from "./billingSchedule";

/**
 * Advances a date by the subscription's billing frequency
 */
export const advanceByFrequency = (
  date: Date,
  subscription: Subscription,
): Date => {
  // Normalize to UTC components to avoid local DST / TZ shifting
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();

  let targetY = y;
  let targetM = m;
  let targetD = d;

  switch (subscription.frequency) {
    case "monthly":
      targetM += 1;
      break;
    case "yearly":
      targetY += 1;
      break;
    case "weekly":
      targetD += 7;
      break;
    case "custom":
      targetD += subscription.customFrequencyDays || 30;
      break;
    default:
      targetM += 1;
  }

  // Construct via Date.UTC so result is UTC midnight
  const advanced = new Date(Date.UTC(targetY, targetM, targetD));

  return advanced;
};

/**
 * Gets the date range for a given month and year
 */
export const getPeriodRange = (month: number, year: number) => {
  // Use UTC to ensure consistent boundaries across environments/timezones
  return {
    start: new Date(Date.UTC(year, month, 1)),
    end: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)),
  };
};

/**
 * Formats a month/year period for display
 */
export const formatPeriodDisplay = (month: number, year: number): string => {
  const date = new Date(year, month, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

/**
 * Calculate the start date of the current billing period
 * @param nextPaymentDate - The next payment date
 * @param frequency - The billing frequency
 * @param customDays - Custom days for 'custom' frequency
 * @returns Date representing the start of the current period
 */
export const calculatePeriodStart = (
  nextPaymentDate: Date,
  frequency: Frequency,
  customDays?: number | null,
): Date => {
  return getPeriodStart(nextPaymentDate, frequency, customDays);
};

/**
 * Calculates the derived next payment date for a subscription based on payment history
 */
export const calculateDerivedNextPayment = (
  subscription: Subscription,
  payments: Array<{ subscription_id: string; payment_date: string }>,
): Date => {
  // Helper to parse ISO date-only strings into stable local calendar dates
  const toDate = (val: string | Date | undefined | null): Date | null => {
    if (!val) return null;

    // If already a Date, normalize to UTC midnight (calendar day preservation)
    if (val instanceof Date) {
      if (isNaN(val.getTime())) return null;
      return new Date(
        Date.UTC(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate()),
      );
    }

    if (typeof val === "string") {
      // Parse strict YYYY-MM-DD as a UTC calendar date at 00:00:00Z
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
      if (m) {
        const [, yy, mm, dd] = m;
        return new Date(Date.UTC(+yy, +mm - 1, +dd));
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }

    return null;
  };

  // Collect all relevant payment dates (subscription + repository)
  const repoPaymentsForSub = payments.filter(
    (p) => p.subscription_id === subscription.id,
  );

  const paidDates = [
    ...(subscription.paymentHistory || []).map((p) => toDate(p.date)),
    ...repoPaymentsForSub.map((p) => toDate(p.payment_date)),
  ].filter((d): d is Date => !!d && !isNaN(d.getTime()));

  // Determine initial next payment candidate
  let nextPaymentDate: Date;
  if (paidDates.length > 0) {
    const latestPaid = paidDates.sort((a, b) => b.getTime() - a.getTime())[0];
    nextPaymentDate = advanceByFrequency(latestPaid, subscription);
  } else {
    nextPaymentDate = toDate(subscription.nextPayment) || new Date();
  }

  // Normalize time to midnight to prevent creeping hour offsets
  // Already UTC midnight by construction

  // Advance until strictly in the future (>= today) relative to mocked "today"
  const today = new Date();
  // Compare using UTC midnight baseline
  today.setUTCHours(0, 0, 0, 0);

  while (nextPaymentDate.getTime() <= today.getTime()) {
    nextPaymentDate = advanceByFrequency(nextPaymentDate, subscription);
    // Ensure stays at UTC midnight after advancing
  }

  return nextPaymentDate;
};
